/**
 * Top-level navigation targets.
 *
 * Extracted from the shell so the URL rules are testable without mounting the
 * whole app -- the alias below in particular is the kind of thing that quietly
 * stops working and nobody notices until a bookmark 404s.
 */

export type View =
  | 'daily'
  | 'backlog'
  | 'inbox'
  | 'plan'
  | 'clients'
  | 'reports'
  | 'billing'
  | 'settings'
  | 'capture';

export const VIEWS: View[] = [
  'daily',
  'backlog',
  'inbox',
  'plan',
  'clients',
  'reports',
  'billing',
  'settings',
];

/**
 * Views reachable from the More menu rather than the bar itself.
 *
 * Four items is what fits the narrowest phone without the row scrolling
 * sideways, and a nav you have to swipe is a nav whose last item nobody finds.
 * These three are the ones you visit deliberately, not the ones you live in.
 */
export const OVERFLOW_VIEWS: View[] = ['clients', 'reports', 'billing', 'settings'];

export const DEFAULT_VIEW: View = 'daily';

export const VIEW_LABELS: Record<View, string> = {
  daily: 'Daily',
  backlog: 'Backlog',
  inbox: 'Inbox',
  plan: 'Plan',
  clients: 'Clients',
  reports: 'Reports',
  billing: 'Billing',
  settings: 'Settings',
  capture: 'Capture',
};

/**
 * Tabs shown in the desktop bar and mobile bottom tab bar.
 * Order is Daily → Plan → Backlog → Inbox (thumb-zone day loop).
 * Everything else lives in More.
 */
export const PRIMARY_VIEWS: View[] = ['daily', 'plan', 'backlog', 'inbox'];

export function isView(value: string | null): value is View {
  return value !== null && (VIEWS as string[]).includes(value);
}

const ALL_VIEWS: View[] = [...VIEWS, 'capture'];

export function isRoutableView(value: string | null): value is View {
  return value !== null && ALL_VIEWS.includes(value as View);
}

/** Which view a query string asks for. Unknown values fall back to Daily. */
export function resolveView(search: string): View {
  const view = new URLSearchParams(search).get('view');
  // Braindump became the Inbox. Old links and bookmarks still land correctly.
  if (view === 'braindump') return 'inbox';
  if (view === 'capture') return 'capture';
  return isView(view) ? view : DEFAULT_VIEW;
}

/** Reports sub-pages: overview (30-day rollups) vs daily journal + breakdown. */
export type ReportsTab = 'overview' | 'daily';

export const REPORTS_TABS: { value: ReportsTab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'daily', label: 'Daily' },
];

export function isReportsTab(value: string | null): value is ReportsTab {
  return value === 'overview' || value === 'daily';
}

/** Which Reports tab a query string asks for. Unknown values fall back to Overview. */
export function resolveReportsTab(search: string): ReportsTab {
  const tab = new URLSearchParams(search).get('tab');
  return isReportsTab(tab) ? tab : 'overview';
}
